/* global fetch */

const API_BASE = (import.meta.env.VITE_API_BASE || "/api").replace(/\/+$/, "");

const TOKEN_STORAGE_KEY = "chronos_jwt_token";
const LOCALE_PREFIXES = new Set(["zh-tw", "en", "ja", "ko"]);

function redirectToForbidden() {
  if (typeof window === "undefined") return;

  const pathname = String(window.location.pathname || "/");
  const segments = pathname.split("/").filter(Boolean);
  const firstSegment = segments[0] || "";
  const prefix = LOCALE_PREFIXES.has(firstSegment) ? firstSegment : "";
  const target = prefix ? `/${prefix}/forbidden` : "/forbidden";

  if (pathname !== target) {
    window.location.replace(target);
  }
}

/**
 * Get stored JWT token
 */
export function getToken() {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Save JWT token to storage
 */
export function saveToken(token) {
  try {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
  } catch (error) {
    console.warn("Failed to save token:", error);
  }
}

/**
 * Clear JWT token from storage
 */
export function clearToken() {
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch (error) {
    console.warn("Failed to clear token:", error);
  }
}

async function request(path, options = {}) {
  const token = getToken();
  const headers = {
    "Content-Type": "application/json",
    ...(options.headers || {}),
  };

  // Add Authorization header if token exists
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    headers,
    ...options,
  });
  let data = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }
  if (res.status === 403) {
    const isRegionBlock =
      !data ||
      data?.blocked ||
      String(data?.reason || "").startsWith("region_blocked:");
    if (isRegionBlock) {
      redirectToForbidden();
      const regionErr = new Error(data?.message || "access forbidden");
      regionErr.regionBlocked = true;
      throw regionErr;
    }
  }
  if (!res.ok || data?.ok === false) {
    const reason = String(data?.reason || "").trim();
    const baseMessage =
      data?.message ||
      (reason
        ? `Request failed: ${reason}`
        : `Request failed: ${res.status}`);
    const pendingHint = data?.pending ? " (pending, retry later)" : "";
    const err = new Error(`${baseMessage}${pendingHint}`);
    err.status = res.status;
    err.payload = data;
    throw err;
  }
  return data;
}

export async function apiHealth() {
  return request("/health", { method: "GET" });
}

export async function apiCheckIpAccess() {
  return request("/access/check-ip", { method: "GET" });
}

export async function apiAuthNonce(address) {
  return request("/auth/nonce", {
    method: "POST",
    body: JSON.stringify({ address }),
  });
}

export async function apiAuthVerify(address, nonce, signature) {
  return request("/auth/verify", {
    method: "POST",
    body: JSON.stringify({ address, nonce, signature }),
  });
}

export async function apiAuthLogout() {
  return request("/auth/logout", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function apiConnectWallet(address) {
  return request("/user/connect", {
    method: "POST",
    body: JSON.stringify({ address }),
  });
}

export async function apiUserDashboard(address) {
  return request(`/user/${encodeURIComponent(address)}/dashboard`, {
    method: "GET",
  });
}

export async function apiCheckin(address, payload = {}) {
  return request(`/user/${encodeURIComponent(address)}/checkin`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function apiRecordAction(address, actionType, payload = {}) {
  return request(`/user/${encodeURIComponent(address)}/actions`, {
    method: "POST",
    body: JSON.stringify({ actionType, ...payload }),
  });
}

export async function apiBuyMarket(address, payload = {}) {
  return apiNotifyShopTx({
    ...payload,
    action: "buy",
    txHash: payload?.txHash || payload?.tx,
    address,
  });
}

export async function apiGetMarketAssets(forceRefresh = false) {
  const suffix = forceRefresh ? "?refresh=1" : "";
  return request(`/market/assets${suffix}`, {
    method: "GET",
  });
}

export async function apiGetMarketNfts(limit = 300, locale = "") {
  const query = new URLSearchParams();
  if (limit) query.set("limit", String(limit));
  if (locale) query.set("locale", String(locale));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return request(`/market/nfts${suffix}`, {
    method: "GET",
  });
}

export async function apiUserNftListings(address, params = {}) {
  const query = new URLSearchParams();
  if (params.nftAddress) query.set("nftAddress", params.nftAddress);
  if (params.limit) query.set("limit", String(params.limit));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return request(`/user/${encodeURIComponent(address)}/nft-listings${suffix}`, {
    method: "GET",
  });
}

export async function apiSyncUserNftHoldings(address, payload = {}) {
  return request(`/user/${encodeURIComponent(address)}/nft-holdings/sync`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function apiMarkUserNftListed(address, payload = {}) {
  return apiNotifyShopTx({
    ...payload,
    action: "list",
    txHash: payload?.txHash || payload?.tx,
    address,
  });
}

export async function apiMarkUserNftOffloaded(address, payload = {}) {
  return apiNotifyShopTx({
    ...payload,
    action: "offload",
    txHash: payload?.txHash || payload?.tx,
    address,
  });
}

export async function apiGetPurchaseHistory(address, params = {}) {
  const query = new URLSearchParams();
  if (params.limit) query.set("limit", String(params.limit));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return request(
    `/user/${encodeURIComponent(address)}/purchase-history${suffix}`,
    {
      method: "GET",
    },
  );
}

export async function apiGetAuctions(params = {}) {
  const query = new URLSearchParams();
  if (params.type) query.set("type", params.type);
  if (params.status) query.set("status", params.status);
  if (params.limit) query.set("limit", String(params.limit));
  if (params.auctionId) query.set("auctionId", String(params.auctionId));
  if (params.nftAddress) query.set("nftAddress", params.nftAddress);
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return request(`/auction/list${suffix}`, { method: "GET" });
}

export async function apiGetUserAuctions(address, params = {}) {
  const query = new URLSearchParams();
  if (params.status) query.set("status", params.status);
  if (params.limit) query.set("limit", String(params.limit));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return request(`/user/${encodeURIComponent(address)}/auctions${suffix}`, {
    method: "GET",
  });
}

export async function apiGetUserAuctionBids(address, params = {}) {
  const query = new URLSearchParams();
  if (params.limit) query.set("limit", String(params.limit));
  if (params.auctionId) query.set("auctionId", String(params.auctionId));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return request(`/user/${encodeURIComponent(address)}/auction-bids${suffix}`, {
    method: "GET",
  });
}

export async function apiGetAuctionRebates(auctionId, params = {}) {
  const query = new URLSearchParams();
  if (params.limit) query.set("limit", String(params.limit));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return request(`/auction/${encodeURIComponent(auctionId)}/rebates${suffix}`, {
    method: "GET",
  });
}

export async function apiGetAuctionEvents(auctionId, params = {}) {
  const query = new URLSearchParams();
  if (params.limit) query.set("limit", String(params.limit));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return request(`/auction/${encodeURIComponent(auctionId)}/events${suffix}`, {
    method: "GET",
  });
}

export async function apiNotifyAuctionTx(payload = {}) {
  return request("/auction/notify", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function apiNotifyShopTx(payload = {}) {
  return request("/shop/notify", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}
